/**
 * ORBIS Admin - API Service
 */

import axios, { type AxiosInstance, type AxiosError } from 'axios';
import type {
  PaginatedResponse,
  AuthTokens,
  User,
  Manufacturer,
  ManufacturerCreate,
  Importer,
  ImporterCreate,
  Laboratory,
  LaboratoryCreate,
  LaboratoryUpdate,
  Equipment,
  EquipmentListItem,
  EquipmentCreate,
  EquipmentUpdate,
  Document,
  UserEntity,
  UserEntityCreate,
} from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

class ApiService {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private isRefreshing: boolean = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api/v1`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Bind generic HTTP methods to this instance
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.put = this.put.bind(this);
    this.delete = this.delete.bind(this);

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const token = this.accessToken || localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Skip refresh logic for auth endpoints to prevent infinite loops
        if (originalRequest?.url?.includes('/auth/refresh') ||
            originalRequest?.url?.includes('/auth/login')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && originalRequest) {
          // If already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.client.request(originalRequest));
              });
            });
          }

          // Try to refresh token
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            this.isRefreshing = true;
            try {
              const tokens = await this.refreshTokenRequest(refreshToken);
              this.setTokens(tokens);

              // Notify all queued requests
              this.refreshSubscribers.forEach(callback => callback(tokens.access_token));
              this.refreshSubscribers = [];

              // Retry the original request
              originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
              return this.client.request(originalRequest);
            } catch {
              this.clearTokens();
              window.location.href = '/login';
              return Promise.reject(error);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            // No refresh token, redirect to login
            this.clearTokens();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Internal refresh request that bypasses interceptors
  private async refreshTokenRequest(refreshToken: string): Promise<AuthTokens> {
    const response = await axios.post<AuthTokens>(
      `${API_URL}/api/v1/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  }

  setTokens(tokens: AuthTokens) {
    this.accessToken = tokens.access_token;
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
  }

  clearTokens() {
    this.accessToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // =====================
  // Auth
  // =====================

  async login(email: string, password: string): Promise<AuthTokens> {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const { data } = await this.client.post<AuthTokens>('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return data;
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    return this.refreshTokenRequest(refreshToken);
  }

  async getCurrentUser(): Promise<User> {
    const { data } = await this.client.get<User>('/auth/me');
    return data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
    this.clearTokens();
  }

  // =====================
  // Manufacturers
  // =====================

  async getManufacturers(params?: {
    search?: string;
    country?: string;
    is_active?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<Manufacturer>> {
    const { data } = await this.client.get('/manufacturers/', { params });
    return data;
  }

  async getManufacturer(id: string): Promise<Manufacturer> {
    const { data } = await this.client.get(`/manufacturers/${id}`);
    return data;
  }

  async createManufacturer(manufacturer: ManufacturerCreate): Promise<Manufacturer> {
    const { data } = await this.client.post('/manufacturers/', manufacturer);
    return data;
  }

  async updateManufacturer(id: string, manufacturer: Partial<ManufacturerCreate>): Promise<Manufacturer> {
    const { data } = await this.client.put(`/manufacturers/${id}`, manufacturer);
    return data;
  }

  async deleteManufacturer(id: string): Promise<void> {
    await this.client.delete(`/manufacturers/${id}`);
  }

  async getManufacturerCountries(): Promise<string[]> {
    const { data } = await this.client.get('/manufacturers/countries');
    return data;
  }

  async uploadManufacturerImage(manufacturerId: string, formData: FormData): Promise<{ public_url: string }> {
    const { data } = await this.client.post(`/manufacturers/${manufacturerId}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  }

  // =====================
  // Importers
  // =====================

  async getImporters(params?: {
    search?: string;
    manufacturer_id?: string;
    is_active?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<Importer>> {
    const { data } = await this.client.get('/importers/', { params });
    return data;
  }

  async getImporter(id: string): Promise<Importer> {
    const { data } = await this.client.get(`/importers/${id}`);
    return data;
  }

  async createImporter(importer: ImporterCreate): Promise<Importer> {
    const { data } = await this.client.post('/importers/', importer);
    return data;
  }

  async updateImporter(id: string, importer: Partial<ImporterCreate>): Promise<Importer> {
    const { data } = await this.client.put(`/importers/${id}`, importer);
    return data;
  }

  async deleteImporter(id: string): Promise<void> {
    await this.client.delete(`/importers/${id}`);
  }

  // =====================
  // Laboratories
  // =====================

  async getLaboratories(params?: {
    search?: string;
    manufacturer_id?: string;
    is_active?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<Laboratory>> {
    const { data } = await this.client.get('/laboratories/', { params });
    return data;
  }

  async getLaboratory(id: string): Promise<Laboratory> {
    const { data } = await this.client.get(`/laboratories/${id}`);
    return data;
  }

  async createLaboratory(laboratory: LaboratoryCreate): Promise<Laboratory> {
    const { data } = await this.client.post('/laboratories/', laboratory);
    return data;
  }

  async updateLaboratory(id: string, laboratory: LaboratoryUpdate): Promise<Laboratory> {
    const { data } = await this.client.put(`/laboratories/${id}`, laboratory);
    return data;
  }

  async deleteLaboratory(id: string): Promise<void> {
    await this.client.delete(`/laboratories/${id}`);
  }

  // =====================
  // Equipment
  // =====================

  async getEquipmentList(params?: {
    search?: string;
    brand?: string;
    model?: string;
    equipment_type?: string;
    manufacturer_id?: string;
    importer_id?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<EquipmentListItem>> {
    const { data } = await this.client.get('/equipment/', { params });
    return data;
  }

  async getEquipment(id: string): Promise<Equipment> {
    const { data } = await this.client.get(`/equipment/${id}`);
    return data;
  }

  async createEquipment(equipment: EquipmentCreate): Promise<Equipment> {
    const { data } = await this.client.post('/equipment/', equipment);
    return data;
  }

  async updateEquipment(id: string, equipment: EquipmentUpdate): Promise<Equipment> {
    const { data } = await this.client.put(`/equipment/${id}`, equipment);
    return data;
  }

  async deleteEquipment(id: string): Promise<void> {
    await this.client.delete(`/equipment/${id}`);
  }

  async submitEquipmentForApproval(id: string): Promise<Equipment> {
    const { data } = await this.client.post(`/equipment/${id}/submit`);
    return data;
  }

  async publishEquipment(id: string): Promise<Equipment> {
    const { data } = await this.client.post(`/equipment/${id}/publish`);
    return data;
  }

  async unpublishEquipment(id: string): Promise<Equipment> {
    const { data } = await this.client.post(`/equipment/${id}/unpublish`);
    return data;
  }

  async getEquipmentTypes(): Promise<string[]> {
    const { data } = await this.client.get('/equipment/types');
    return data;
  }

  async getEquipmentBrands(): Promise<string[]> {
    const { data } = await this.client.get('/equipment/brands');
    return data;
  }

  // =====================
  // Documents
  // =====================

  async getEquipmentDocuments(equipmentId: string, params?: Record<string, string>): Promise<PaginatedResponse<Document>> {
    const { data } = await this.client.get(`/documents/equipment/${equipmentId}`, { params });
    return data;
  }

  async getDocument(id: string): Promise<Document> {
    const { data } = await this.client.get(`/documents/${id}`);
    return data;
  }

  async uploadDocument(
    equipmentId: string,
    file: File,
    documentType: string,
    isConfidential: boolean = false
  ): Promise<Document> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('is_confidential', String(isConfidential));
    // Default record type for legacy uploads
    formData.append('record_type', 'COMMERCIAL');
    formData.append('sensitivity_level', isConfidential ? 'ORBIS_ONLY' : 'CLIENT_RESTRICTED');

    const { data } = await this.client.post(`/documents/equipment/${equipmentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  // V3 Document Upload with full options
  async uploadDocumentV3(equipmentId: string, formData: FormData): Promise<Document> {
    const { data } = await this.client.post(`/documents/equipment/${equipmentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  async deleteDocument(id: string): Promise<void> {
    await this.client.delete(`/documents/${id}`);
  }

  getDocumentDownloadUrl(id: string, password?: string): string {
    const params = password ? `?password=${encodeURIComponent(password)}` : '';
    return `${API_URL}/api/v1/documents/${id}/download${params}`;
  }

  // V3 Document Lifecycle
  async approveDocument(id: string, options: { set_public_eligible?: boolean }): Promise<Document> {
    const { data } = await this.client.post(`/documents/${id}/approve`, options);
    return data;
  }

  async rejectDocument(id: string, options: { reason: string }): Promise<Document> {
    const { data } = await this.client.post(`/documents/${id}/reject`, options);
    return data;
  }

  async supersedeDocument(oldDocumentId: string, newDocumentId: string, options: { reason: string }): Promise<Document> {
    const { data } = await this.client.post(`/documents/${oldDocumentId}/supersede`, options, {
      params: { new_document_id: newDocumentId },
    });
    return data;
  }

  async updateDocumentSensitivity(id: string, sensitivityLevel: string): Promise<Document> {
    const { data } = await this.client.put(`/documents/${id}/sensitivity`, null, {
      params: { sensitivity_level: sensitivityLevel },
    });
    return data;
  }

  // V3 Review Queue
  async getDocumentReviewQueue(params?: {
    page?: number;
    per_page?: number;
  }): Promise<{
    items: any[];
    total: number;
    pending_count: number;
  }> {
    const { data } = await this.client.get('/documents/review-queue', { params });
    return data;
  }

  // V3 Document History
  async getDocumentHistory(id: string): Promise<{
    document_id: string;
    current_version: number;
    history: any[];
  }> {
    const { data } = await this.client.get(`/documents/${id}/history`);
    return data;
  }

  // V3 Document Visibility (per-country)
  async setDocumentVisibility(documentId: string, countryId: string, isPublic: boolean, reason?: string): Promise<any> {
    const { data } = await this.client.post(`/documents/${documentId}/visibility`, {
      country_id: countryId,
      is_public: isPublic,
      reason,
    });
    return data;
  }

  async getDocumentVisibility(documentId: string): Promise<any[]> {
    const { data } = await this.client.get(`/documents/${documentId}/visibility`);
    return data;
  }

  // =====================
  // Users
  // =====================

  async getUsers(params?: {
    search?: string;
    role?: string;
    is_active?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<User>> {
    const { data } = await this.client.get('/users/', { params });
    return data;
  }

  async getUser(id: string): Promise<User> {
    const { data } = await this.client.get(`/users/${id}`);
    return data;
  }

  async createUser(user: {
    email: string;
    password: string;
    full_name: string;
    role: string;
    is_approver?: boolean;
    client_id?: string;
    regulator_authority_id?: string;
  }): Promise<User> {
    const { data } = await this.client.post('/users/', user);
    return data;
  }

  async updateUser(id: string, user: {
    email?: string;
    full_name?: string;
    role?: string;
    is_active?: boolean;
    is_approver?: boolean;
    client_id?: string;
    regulator_authority_id?: string;
  }): Promise<User> {
    const { data } = await this.client.put(`/users/${id}`, user);
    return data;
  }

  async deactivateUser(id: string): Promise<void> {
    await this.client.delete(`/users/${id}`);
  }

  async activateUser(id: string): Promise<User> {
    const { data } = await this.client.post(`/users/${id}/activate`);
    return data;
  }

  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    await this.client.post(`/users/${id}/reset-password`, { new_password: newPassword });
  }

  async getUserStats(): Promise<{ total: number; by_role: Record<string, number> }> {
    const { data } = await this.client.get('/users/stats');
    return data;
  }

  // User-Entity Relationships
  async getUserEntities(params?: { user_id?: string }): Promise<UserEntity[]> {
    const { data } = await this.client.get('/user-entities/', { params });
    return data;
  }

  async createUserEntity(userEntity: UserEntityCreate): Promise<UserEntity> {
    const { data } = await this.client.post('/user-entities/', userEntity);
    return data;
  }

  async updateUserEntity(userId: string, entityId: string, entityType: string, userEntity: Partial<UserEntity>): Promise<UserEntity> {
    const { data } = await this.client.put(`/user-entities/${userId}/${entityId}/${entityType}`, userEntity);
    return data;
  }

  async deleteUserEntity(userId: string, entityId: string, entityType: string): Promise<void> {
    await this.client.delete(`/user-entities/${userId}/${entityId}/${entityType}`);
  }

  // =====================
  // Audit Logs
  // =====================

  async getAuditLogs(params?: {
    user_id?: string;
    action?: string;
    entity_type?: string;
    entity_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<AuditLog>> {
    const { data } = await this.client.get('/audit-logs/', { params });
    return data;
  }

  async getAuditLogStats(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<AuditLogStats> {
    const { data } = await this.client.get('/audit-logs/stats', { params });
    return data;
  }

  async getRecentLogins(limit?: number): Promise<{ logins: AuditLog[] }> {
    const { data } = await this.client.get('/audit-logs/recent-logins', { params: { limit } });
    return data;
  }

  async getEntityAuditHistory(entityType: string, entityId: string): Promise<{
    entity_type: string;
    entity_id: string;
    logs: AuditLog[];
  }> {
    const { data } = await this.client.get(`/audit-logs/entity/${entityType}/${entityId}`);
    return data;
  }

  // =====================
  // Generic HTTP Methods
  // =====================

  /**
   * Generic GET request
   */
  async get(url: string, config?: any) {
    return this.client.get(url, config);
  }

  /**
   * Generic POST request
   */
  async post(url: string, data?: any, config?: any) {
    return this.client.post(url, data, config);
  }

  /**
   * Generic PUT request
   */
  async put(url: string, data?: any, config?: any) {
    return this.client.put(url, data, config);
  }

  /**
   * Generic PATCH request
   * v2.4: Added for client preferences updates
   */
  async patch(url: string, data?: any, config?: any) {
    return this.client.patch(url, data, config);
  }

  /**
   * Generic DELETE request
   */
  async delete(url: string, config?: any) {
    return this.client.delete(url, config);
  }

  getAuditLogExportUrl(params?: {
    user_id?: string;
    action?: string;
    entity_type?: string;
    start_date?: string;
    end_date?: string;
  }): string {
    const searchParams = new URLSearchParams();
    if (params?.user_id) searchParams.append('user_id', params.user_id);
    if (params?.action) searchParams.append('action', params.action);
    if (params?.entity_type) searchParams.append('entity_type', params.entity_type);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    const query = searchParams.toString();
    return `${API_URL}/api/v1/audit-logs/export/csv${query ? `?${query}` : ''}`;
  }

  // ===========================================
  // AI Features
  // ===========================================

  /**
   * Check if AI extraction features are available
   */
  async getAIStatus(): Promise<AIStatusResponse> {
    const response = await this.client.get('/ai/status');
    return response.data;
  }

  /**
   * Extract technical specifications from a PDF datasheet
   */
  async extractDatasheet(file: File): Promise<ExtractDatasheetResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post('/ai/extract-datasheet', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minute timeout for AI processing
    });
    return response.data;
  }
}

// Types for audit logs
export interface AuditLog {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface AuditLogStats {
  total_logs: number;
  by_action: Record<string, number>;
  recent_logins: number;
  recent_failed_logins: number;
}

// Types for AI features
export interface AIStatusResponse {
  available: boolean;
  message: string;
}

export interface ExtractedFrequencyBand {
  band: string;
  range: string | null;
  power?: string | null;  // v2.10: Per-band power (advanced field)
}

// v2.10: Extracted radio technology for multi-radio products
export interface ExtractedRadioTechnology {
  name: string;
  modulation?: string | null;
  eirp_value?: number | null;
  eirp_unit?: string | null;
  antenna_gain_dbi?: number | null;
  max_power_value?: number | null;
  max_power_unit?: string | null;
  frequency_bands: ExtractedFrequencyBand[];
  bandwidth?: string | null;
}

export interface ExtractedSpecifications {
  brand: string | null;
  model: string | null;
  commercial_name: string | null;
  equipment_type: string | null;
  // v2.10: Radio technologies array (new primary field)
  radio_technologies?: ExtractedRadioTechnology[];
  // Legacy fields (deprecated in v2.10 but kept for backward compatibility)
  technology_modulation: string | null;
  bandwidth: string | null;
  antenna_gain_dbi: number | null;
  eirp_dbm: number | null;
  max_power_w: number | null;
  frequency_bands: ExtractedFrequencyBand[];
  modules: string[];
  confidence: 'low' | 'medium' | 'high';
  notes: string | null;
}

export interface ExtractDatasheetResponse {
  success: boolean;
  specifications: ExtractedSpecifications | null;
  error: string | null;
  text_length: number;
}

export const api = new ApiService();
export default api;

